import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// The wording options mirror the backend reminder milestones (7d / 3d / 1d).
const TIMING_OPTIONS = [
  { value: "7d", label: "The seminar is in one week", short: "1 week" },
  { value: "3d", label: "The seminar is in 3 days", short: "3 days" },
  { value: "1d", label: "The seminar is tomorrow", short: "tomorrow" },
];

// Confirmation dialog that also lets the admin pick the reminder wording before
// re-sending a real email to the selected registrants.
export const ResendReminderDialog = ({ count, selectedItems = [], onConfirm, resending, trigger }) => {
  const [milestone, setMilestone] = useState("3d");

  // How many of the selected registrants have already had this exact reminder?
  const alreadyCount = selectedItems.filter((it) =>
    Array.isArray(it.reminders_sent) && it.reminders_sent.includes(milestone)
  ).length;
  const shortLabel = TIMING_OPTIONS.find((o) => o.value === milestone)?.short || milestone;

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      <AlertDialogContent data-testid="resend-reminder-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>
            Resend reminder to {count} registrant{count === 1 ? "" : "s"}?
          </AlertDialogTitle>
          <AlertDialogDescription>
            This sends a real reminder email to each selected registrant now. Choose the wording
            that fits how close the seminar is.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="py-1">
          <label className="block text-sm font-medium text-[#122131] mb-1.5">Reminder wording</label>
          <Select value={milestone} onValueChange={setMilestone}>
            <SelectTrigger data-testid="resend-milestone-select" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIMING_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value} data-testid={`resend-milestone-${o.value}`}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {alreadyCount > 0 && (
            <div
              data-testid="resend-already-sent-warning"
              className="mt-3 flex items-start gap-2 rounded-lg border border-[rgba(247,184,56,0.5)] bg-[rgba(247,184,56,0.12)] px-3 py-2 text-sm text-[#8a5a00]"
            >
              <AlertTriangle size={16} className="mt-0.5 shrink-0 text-[#b8860b]" />
              <span>
                {alreadyCount} of {count} selected {alreadyCount === 1 ? "person has" : "people have"} already
                received the "{shortLabel}" reminder. Resending will email {alreadyCount === 1 ? "them" : "them"} again.
              </span>
            </div>
          )}
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="resend-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction
            data-testid="resend-confirm"
            disabled={resending}
            onClick={() => onConfirm(milestone)}
            className="bg-[#1F9188] hover:bg-[#126F6A] focus:ring-[#1F9188]"
          >
            Send reminder
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
